import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  PieChart, Pie, Cell, Tooltip as RechartTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { tasksAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';

// ── Brand ─────────────────────────────────────────────────────────────────────
const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

// ── Chart colours ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'To Do':       '#94a3b8',
  'In Progress': '#f59e0b',
  'Review':      '#7c3aed',
  'Done':        '#16a34a',
  'Cancelled':   '#9ca3af',
};
const PRIORITY_COLORS = {
  Critical: '#dc2626',
  High:     '#ea580c',
  Medium:   '#2563eb',
  Low:      '#64748b',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${accent || GOLD}`, flex: 1, minWidth: 160 }}>
      <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: NAVY }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children, action }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: NAVY }}>{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

// ── Completion rate colour ─────────────────────────────────────────────────────
function rateColor(pct) {
  if (pct >= 80) return '#16a34a';
  if (pct >= 50) return '#d97706';
  return '#dc2626';
}

// ── Custom pie label ──────────────────────────────────────────────────────────
function PieLabel({ cx, cy, midAngle, outerRadius, value }) {
  if (!value) return null;
  const RAD = Math.PI / 180;
  const r = outerRadius + 22;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={12} fill="#374151">{value}</text>;
}

// ─────────────────────────────────────────────────────────────────────────────
export default function TaskReports() {
  const navigate = useNavigate();
  const { isAdminOrAbove, isManager, isSVP, isRCM, isMD } = useRole();
  const canView = isAdminOrAbove || isManager || isSVP || isRCM || isMD;

  const [stats,    setStats]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    tasksAPI.getReportStats()
      .then(r => setStats(r.data?.data || null))
      .catch(() => toast.error('Failed to load report data'))
      .finally(() => setLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ color: NAVY, margin: '0 0 8px' }}>Access Restricted</h2>
        <p>You don't have permission to view task reports.</p>
        <button onClick={() => navigate('/tasks')} style={{ marginTop: 12, padding: '8px 20px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
          Back to Tasks
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <div style={{ width: 36, height: 36, border: `3px solid ${GOLD}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (!stats) return null;

  // ── Chart data ───────────────────────────────────────────────────────────
  const statusPieData = Object.entries(stats.by_status)
    .map(([name, value]) => ({ name, value }))
    .filter(d => d.value > 0);

  const priorityBarData = Object.entries(stats.by_priority)
    .map(([name, count]) => ({ name, count }));

  const categoryBarData = (stats.by_category || [])
    .slice(0, 12)
    .map(c => ({ name: c.category, count: c.total }));

  // ── Export handler ───────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await tasksAPI.exportCSV();
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `tasks-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: NAVY, margin: 0 }}>Task Reports</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>Analytics across your accessible tasks</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate('/tasks')}
            style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            ← Tasks
          </button>
          <button onClick={handleExport} disabled={exporting}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: GOLD, color: NAVY, fontWeight: 700, fontSize: 13, cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.7 : 1 }}>
            {exporting ? 'Exporting…' : '⬇ Export CSV'}
          </button>
        </div>
      </div>

      {/* ── Stat Cards ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Tasks"         value={stats.total_tasks}           accent={NAVY} />
        <StatCard label="Completion Rate"     value={`${stats.completion_rate}%`} accent="#16a34a" sub="of non-cancelled tasks" />
        <StatCard label="Overdue"             value={stats.overdue_count}         accent="#dc2626" />
        <StatCard label="Avg Days to Complete" value={stats.avg_completion_days}  accent="#7c3aed" sub="days from creation" />
      </div>

      {/* ── Charts row ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 24, marginBottom: 0 }}>

        {/* Chart 1 — Status Pie */}
        <SectionCard title="Task Status Breakdown">
          {statusPieData.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  labelLine={false}
                  label={PieLabel}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <RechartTooltip formatter={(v, n) => [v, n]} />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>No data</p>
          )}
        </SectionCard>

        {/* Chart 2 — Priority Bar */}
        <SectionCard title="Tasks by Priority">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={priorityBarData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <RechartTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48}>
                {priorityBarData.map((entry) => (
                  <Cell key={entry.name} fill={PRIORITY_COLORS[entry.name] || NAVY} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {/* ── Team Performance ───────────────────────────────────────────────── */}
      <SectionCard title="Team Performance">
        {stats.by_member?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Team Member','Assigned','Done','Overdue','In Progress','Completion Rate'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Team Member' ? 'left' : 'right', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.by_member.map((m, i) => (
                  <tr key={m.user_id} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: NAVY }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', background: NAVY, color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, flexShrink: 0 }}>
                          {m.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        {m.name}
                      </div>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#374151' }}>{m.total_assigned}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>{m.done}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: m.overdue > 0 ? '#dc2626' : '#94a3b8', fontWeight: m.overdue > 0 ? 600 : 400 }}>{m.overdue}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#d97706' }}>{m.in_progress}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <span style={{ background: `${rateColor(m.completion_rate)}18`, color: rateColor(m.completion_rate), borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 12 }}>
                        {m.completion_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No team data</p>
        )}
      </SectionCard>

      {/* ── Tasks by Category ──────────────────────────────────────────────── */}
      {categoryBarData.length > 0 && (
        <SectionCard title="Tasks by Category">
          <ResponsiveContainer width="100%" height={Math.max(180, categoryBarData.length * 36)}>
            <BarChart data={categoryBarData} layout="vertical" margin={{ top: 0, right: 40, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <RechartTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="count" fill={NAVY} radius={[0, 4, 4, 0]} maxBarSize={22} label={{ position: 'right', fontSize: 11, fill: '#64748b' }} />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>
      )}

      {/* ── Recent Completions ─────────────────────────────────────────────── */}
      <SectionCard title="Recent Completions">
        {stats.recent_completions?.length ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Task Number','Title','Assigned To','Completed On'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.recent_completions.map((t, i) => (
                  <tr key={String(t._id)} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, color: '#475569' }}>{t.task_number}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: NAVY }}>{t.title}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{t.assigned_to_name}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{fmt(t.completed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: '24px 0' }}>No completed tasks yet</p>
        )}
      </SectionCard>
    </div>
  );
}
